import {
  Box,
  Checkbox,
  Input,
  InputGroup,
  InputRightElement,
  List,
  ListItem,
  useDisclosure,
  useOutsideClick,
} from '@chakra-ui/react';

import {
  ArrowDownBold,
  ArrowUpBold,
  DropdownOption,
  shadowStyles,
} from '@aslanpay/portal-common-lib';
import { useRef, useState } from 'react';

import { DropdownMultiSelectInputProps } from './types';

function DropdownMultiSelectInput({
  placeholder,
  defaultValue,
  options,
  onChange,
  onBlur,
}: DropdownMultiSelectInputProps) {
  const ref = useRef<HTMLDivElement>(null);

  const { isOpen, onToggle, onClose } = useDisclosure();
  const [optionsSelected, setOptionsSelected] = useState<DropdownOption[]>(
    options.filter((option) => option.value !== undefined && defaultValue?.includes(option.value))
  );

  const handleOptionSelected = (item: DropdownOption) => {
    const changedOptionsSelected = optionsSelected.some((option) => option.value === item.value)
      ? optionsSelected.filter((option) => option.value !== item.value)
      : [...optionsSelected, item];

    setOptionsSelected(changedOptionsSelected);

    onChange(
      changedOptionsSelected
        .map((option) => option.value)
        .filter((value): value is string | number => value !== undefined)
    );

    onBlur();
  };

  useOutsideClick({
    ref,
    handler: () => {
      onClose();
      onBlur();
    },
  });

  return (
    <Box ref={ref} position="relative">
      <InputGroup onClick={onToggle}>
        <Input
          readOnly
          cursor="pointer"
          type="text"
          placeholder={placeholder}
          value={optionsSelected.map((option) => option.text).join(', ')}
          onBlur={onBlur}
        />
        <InputRightElement cursor="pointer" h="full">
          {isOpen ? (
            <ArrowUpBold color="brand.darkGray" boxSize="24px" />
          ) : (
            <ArrowDownBold color="brand.darkGray" boxSize="24px" />
          )}
        </InputRightElement>
      </InputGroup>

      {isOpen && (
        <List
          cursor="pointer"
          zIndex={998}
          position="absolute"
          w="full"
          h="auto"
          maxH="250px"
          mt="5px"
          overflow="auto"
          bgColor="brand.white"
          borderRadius="5px"
          boxShadow={shadowStyles.dropdownMenu}
        >
          {options?.map((item: DropdownOption) => (
            <ListItem
              key={item.value}
              p="10px"
              borderBottom="1px solid"
              borderColor="brand.lightGray1"
              textStyle="smallMedium"
              color="brand.black"
              onClick={() => handleOptionSelected(item)}
              _hover={{ bgColor: 'brand.lightGray1' }}
              transition="background-color 0.2s ease-in-out"
            >
              <Checkbox
                pointerEvents="none"
                isChecked={optionsSelected.some((option) => option.value === item.value)}
                size="md"
              >
                {item.text}
              </Checkbox>
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
}

export default DropdownMultiSelectInput;
